import type { MaybeRefOrGetter } from 'vue'

export interface BreadcrumbItem {
  /**
   * Visible label, already translated. Resolve `t()` at page setup rather than
   * passing a key — see the note on lazy i18n in `seoTitle` (utils/index.ts):
   * during head render vue-i18n can still return the key itself, and a raw
   * `page.home.services` must never reach a crawler's BreadcrumbList.
   */
  label: string
  /**
   * Locale-less app path, e.g. `/business-types`. Run through `localePath()`
   * here so callers never hand-build the `/en` / `/ar` prefix themselves.
   * Omit on the last crumb — the current page is never a link.
   */
  to?: string
}

export interface ResolvedBreadcrumb {
  label: string
  /** Locale-prefixed path. `undefined` on the current page. */
  to?: string
  isCurrent: boolean
}

/**
 * Builds a breadcrumb trail and registers the matching `BreadcrumbList`
 * JSON-LD, so the DOM trail and the structured data can't drift apart —
 * markup with no corresponding on-page content is a structured-data violation.
 *
 * Render the returned `crumbs` with `<CommonBreadcrumbs>`, which is `sr-only`
 * by design: the trail is deliberately absent from the UI, but stays in the DOM
 * and the accessibility tree so the JSON-LD still describes real content and
 * the parent links stay crawlable. Always render it — never emit the JSON-LD
 * on its own.
 */
export function useBreadcrumbs(items: MaybeRefOrGetter<BreadcrumbItem[]>) {
  const localePath = useLocalePath()
  const { frontendUrl } = useRuntimeConfig().public

  const crumbs = computed<ResolvedBreadcrumb[]>(() => {
    // Drop blanks so a page can pass a crumb whose data hasn't loaded (an
    // absent product name) without leaving a hole in the trail.
    const list = toValue(items).filter(item => !!item.label)

    return list.map((item, index) => {
      const isCurrent = index === list.length - 1
      return {
        label: item.label,
        to: isCurrent || !item.to ? undefined : localePath(item.to),
        isCurrent,
      }
    })
  })

  const jsonLd = computed(() => {
    // One crumb is not a hierarchy — Google needs at least two levels before
    // it will render a trail, so emitting it would be noise.
    if (crumbs.value.length < 2) return null

    const origin = (frontendUrl as string | undefined)?.replace(/\/$/, '') ?? ''

    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.value.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.label,
        // The last crumb deliberately carries no `item`: Google infers the
        // current URL, and this keeps the markup honest about which crumbs
        // are links. `origin` is empty only when FRONTEND_URL is unset (dev),
        // where a relative URL still resolves against the page.
        ...(crumb.to ? { item: `${origin}${crumb.to}` } : {}),
      })),
      // Labels carry API data (business and product names) — escape "<" so a
      // name can't break out of the <script> block.
    }).replace(/</g, '\\u003c')
  })

  const headScripts = computed(() =>
    jsonLd.value
      ? [{ type: 'application/ld+json', innerHTML: jsonLd.value }]
      : [],
  )

  useHead({ script: headScripts })

  return { crumbs }
}
